<template>
    <div v-if="loading">
        {{ loadingMessage }}
    </div>
    <div v-else class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditScenario.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditScenarios">{{ $t('componentEditScenario.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditScenario.title') }}</li>
                    </ol>
                </div>
            </div>

            <!-- Form [start] -->
            <form id="componentForm" name="componentForm" class="pb-4" @submit.prevent="handleSubmit">
                <div class="row">
                    <div class="col-xxl-8 col-xl-7 d-flex flex-column">
                        <div class="card flex-grow-1">
                            <div class="card-header pb-0">
                                <div class="d-sm-flex align-items-center justify-content-between">
                                    <h4 class="header-title">{{ $t('common.generalConfig') }}</h4>
                                    <div class="mt-2 mt-sm-0">
                                        <!-- [IF] Scénario actif -->
                                        <button v-if="scenario.is_active" type="button" class="btn btn-outline-danger" @click="toggleScenario()">
                                            <i class="mdi mdi-motion-pause-outline"></i> {{ $t('componentEditScenario.suspend') }}
                                        </button>
                                        <button v-if="!scenario.is_active" type="button" class="btn btn-outline-success" @click="toggleScenario()">
                                            <i class="mdi mdi-motion-play-outline text-success"></i> {{ $t('componentEditScenario.resume') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body">
                                <div class="row align-items-end">
                                    <div class="mb-3">
                                        <label for="nom-scenario" class="form-label">{{ $t('componentEditScenario.scenarioName') }}</label>
                                        <input type="text" class="form-control" :placeholder="$t('componentEditScenario.scenarioName')" v-model="scenario.label" required maxlength="32">
                                    </div>
                                    <!-- Ouvre ce scénario aux tâches planifiées du gestionnaire -->
                                    <div class="mb-3">
                                        <div class="form-check form-switch">
                                            <input class="form-check-input" type="checkbox" id="scenario-manager-controllable" v-model="scenario.manager_controllable">
                                            <label class="form-check-label" for="scenario-manager-controllable">{{ $t('componentEditScenario.managerControllable') }}</label>
                                        </div>
                                        <div class="form-text">{{ $t('componentEditScenario.managerControllableHint') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Tableau des conditions -->
                        <div class="card flex-shrink-0 border border-info">
                            <div class="card-header bg-info">
                                <div class="d-sm-flex align-items-center justify-content-between">
                                    <h4 class="header-title text-white">{{ $t('componentEditScenario.conditions') }}</h4>
                                    <!-- Ajouter une condition -->
                                    <div class="mt-2 mt-sm-0">
                                        <button type="button" class="btn btn-sm btn-soft-info" @click="addCondition" data-bs-toggle="modal" data-bs-target="#openConditionModal" :disabled="scenario.conditions.length == 5">{{ $t('componentEditScenario.addCondition') }}</button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body p-0">
                                <table id="table-conditions" class="table table-striped table-centered m-0">
                                    <thead>
                                        <tr>
                                            <th>{{ $t('componentEditScenario.conditionDescription') }}</th>
                                            <th>{{ $t('common.active') }}</th>
                                            <th>&nbsp;</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="scenario.conditions.length === 0" class="odd">
                                            <td valign="top" colspan="6" class="text-center">{{ $t('componentEditScenario.noCondition') }}</td>
                                        </tr>
                                        <ConditionRow v-for="condition in scenario.conditions" :key="condition.id" :conditionValue="condition" :onEdit="editCondition" :onDelete="deleteCondition" />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <!-- Tableau des actions -->
                        <div class="card flex-shrink-0 border border-primary">
                            <div class="card-header bg-primary">
                                <div class="d-sm-flex align-items-center justify-content-between">
                                    <h4 class="header-title text-white">{{ $t('componentEditScenario.actions') }}</h4>
                                    <!-- Ajouter une action -->
                                    <div class="mt-2 mt-sm-0">
                                        <button type="button" class="btn btn-sm btn-soft-primary" @click="addAction" data-bs-toggle="modal" data-bs-target="#openActionModal" :disabled="scenario.actions.length == 5">{{ $t('componentEditScenario.addAction') }}</button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body p-0">
                                <table id="table-actions" class="table table-striped table-centered m-0">
                                    <thead>
                                        <tr>
                                            <th>{{ $t('componentEditScenario.actionDescription') }}</th>
                                            <th>{{ $t('common.active') }}</th>
                                            <th>&nbsp;</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="scenario.actions.length === 0" class="odd">
                                            <td valign="top" colspan="6" class="text-center">{{ $t('componentEditScenario.noAction') }}</td>
                                        </tr>
                                        <ActionRow v-for="action in scenario.actions" :key="action.id" :actionValue="action" :onEdit="editAction" :onDelete="deleteAction" />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="col-xxl-4 col-xl-5">
                        <div class="card card-h-100">
                            <div class="card-header pb-0 d-flex align-items-center justify-content-between">
                                <h4 class="header-title">{{ $t('componentEditHub.composition') }}</h4>
                                <span v-if="hub.hub_state === 1" class="badge bg-success font-14 px-2">{{ $t('common.connected') }}</span>
                                <span v-else class="badge bg-danger font-14 px-2">{{ $t('common.disconnected') }}</span>
                            </div>
                            <div class="card-body ps-1">
                                <div ref="tree" class="component-treeview tree-components-lite">
                                    <ul>
                                        <li data-jstree='{ "icon" : "mdi mdi-server-network text-primary", "opened" : true }'>
                                            <div class="tree-row">
                                                <div id="hub_node" class="tree-cell component-name fw-bold text-primary">{{ hub.label }}</div>
                                                <div class="tree-cell component-action">
                                                    <TreeButton :path="hub.path()" />
                                                </div>
                                            </div>
                                            <ul>
                                                <li v-for="scenario in hub.scenarios" :key="`${scenario.id}`" :data-jstree="getScenarioIcon(scenario.id)">
                                                    <div :class="['tree-row', { active: isSelected(scenario.id) }]">
                                                        <div :id="scenario.is_active ? 'current_node' : null" class="tree-cell component-name fw-bold text-dark">{{ scenario.label }}</div>
                                                        <div class="tree-cell component-state">
                                                            <strong v-if="scenario.is_active" class="text-success">{{ $t('common.active') }}</strong>
                                                            <strong v-else class="text-danger">{{ $t('common.inactive') }}</strong>
                                                        </div>
                                                        <div class="tree-cell component-action">
                                                            <TreeButton v-if="!isSelected(scenario.id)" :path="scenario.path(facilityId, hub.id)" />
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link :to="pathToInstallationEditScenarios" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
            <!-- Form [end] -->
        </div>
    </div>
    <ConditionModal ref="conditionModal" @onConditionUpdated="onConditionUpdated" />
    <ActionModal ref="actionModal" @onActionUpdated="onActionUpdated" :hub="hub" />
</template>

<script>
import { computed, ref, onMounted, nextTick } from 'vue';
import { useClientStore } from '@/stores/clientStore';
import { useUserStore } from '@/stores/userStore';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast'
import ConditionModal from '@/modals/conditionModal';
import ActionModal from '@/modals/actionModal';
import ConditionRow from '@/components/conditionRow';
import ActionRow from '@/components/actionRow';
import TreeButton from '@/components/treeButton';
import Scenario from '@/models/scenario';
import Condition from '@/models/condition';
import Action from '@/models/action';
import $ from 'jquery';

export default {
    name: 'ComponentEditScenario',
    components: {
        ConditionModal,
        ActionModal,
        ConditionRow,
        ActionRow,
        TreeButton,
    },
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const loading = ref(true);
        const loadingMessage = ref(t('common.loading'));

        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const scenarioId = parseInt(route.query.id);
        const scenario = ref(null);

        const conditionModal = ref(null);
        const actionModal = ref(null);

        const pathToInstallationEditScenarios = computed(() => {
            return `/installation-edit?id=${facilityId}&action=scenarios`;
        });

        const toggleScenario = () => {
            scenario.value.is_active = !scenario.value.is_active;
        };

        const addCondition = () => {
            conditionModal.value.setCondition(new Condition(-1));
        };

        const editCondition = (condition) => {
            conditionModal.value.setCondition(condition);
        }

        const deleteCondition = (id) => {
            const index = scenario.value.conditions.findIndex(c => c.id === id);
            if (index != -1) {
                scenario.value.conditions.splice(index, 1);
            }
        };

        const onConditionUpdated = (condition) => {
            if (condition.id == -1) {
                condition.id = scenario.value.conditions.reduce((max, current) => { return current.id > max ? current.id : max; }, 0) + 1;
                scenario.value.conditions.push(condition);
            } else {
                const index = scenario.value.conditions.findIndex(c => c.id == condition.id);
                if (index != -1) {
                    scenario.value.conditions[index] = condition;
                }
            }
        };

        const addAction = () => {
            actionModal.value.setAction(new Action(-1));
        };

        const editAction = (action) => {
            actionModal.value.setAction(action);
        };

        const deleteAction = (id) => {
            const index = scenario.value.actions.findIndex(a => a.id === id);
            if (index != -1) {
                scenario.value.actions.splice(index, 1);
            }
        };

        const onActionUpdated = (action) => {
            if (action.id == -1) {
                action.id = scenario.value.actions.reduce((max, current) => { return current.id > max ? current.id : max; }, 0) + 1;
                scenario.value.actions.push(action);
            } else {
                const index = scenario.value.actions.findIndex(a => a.id == action.id);
                if (index != -1) {
                    scenario.value.actions[index] = action;
                }
            }
        };

        const isSelected = (id) => {
            return id === scenarioId ? true : undefined;
        };

        const getScenarioIcon = (id) => {
            return JSON.stringify({
                icon: 'mdi mdi-movie-open text-dark',
                selected: isSelected(id)
            });
        };

        const tree = ref(null);

        onMounted(async () => {
            try {
                const clientStore = useClientStore();
                await clientStore.loadClientData(facilityId, hubId);

                if(scenarioId == 0) {
                    scenario.value = new Scenario();
                } else {
                    scenario.value = userStore.getScenario(hub, scenarioId);
                }

                loading.value = false;

                await nextTick();

                $(tree.value).jstree();

                $(tree.value).on('click', '.tree-button', (event) => {
                    const jsonURI = $(event.currentTarget).attr('data-url');
                    const uri = JSON.parse(jsonURI);
                    router.push({ path: uri.path, query: uri.params });
                });

            } catch (e) {
                loadingMessage.value = e;
            }
        });

        const handleSubmit = async () => {
            try {
                await userStore.updateScenario(facilityId, hub, scenario.value);
                const node = document.getElementById("current_node");
                if(node) {
                    node.innerHTML = scenario.value.label;
                }
                showToast('success', t('common.updateSuccess'));
            } catch (e) {
                showToast('error', e);
            }
        };

        return {
            pathToInstallationEditScenarios,
            facilityId,
            hub,
            scenario,
            toggleScenario,
            conditionModal,
            addCondition,
            editCondition,
            deleteCondition,
            onConditionUpdated,
            actionModal,
            addAction,
            editAction,
            deleteAction,
            onActionUpdated,
            tree,
            isSelected,
            getScenarioIcon,
            handleSubmit,
            loading,
            loadingMessage,
        }
    },
}
</script>

<style scoped>
@media (min-width: 1199px) {

    table th:nth-child(2),
    th:last-child {
        width: 100px;
        min-width: 100px;
        text-align: center;
    }
}
</style>
